import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { User, UserPreferences } from '../types';
import { authService } from '../services/auth';
import type { AuthConfig } from '../services/auth';
import { useQueryClient } from '@tanstack/react-query';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  authConfig: AuthConfig | null;
  login: (googleToken: string) => Promise<void>;
  register: (userData: { username: string; email: string; name: string; password: string }) => Promise<void>;
  loginWithPassword: (username: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  updateUserSettings: (userData: { name?: string; preferences?: UserPreferences; about_me?: string | null }) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null);
  const queryClient = useQueryClient();

  // Helper function to clear all cached data
  const clearCache = () => {
    queryClient.clear();
  };

  // Initialize theme based on user preferences
  useEffect(() => {
    if (user?.preferences?.darkMode === true) {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
    }
  }, [user, user?.preferences, user?.preferences?.darkMode]);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Get auth configuration
        const config = await authService.getAuthConfig();
        setAuthConfig(config);

        // If authentication is disabled, create a mock user and skip auth
        if (!config.auth_enabled) {
          // Load stored preferences from localStorage for development mode
          const storedPreferences = localStorage.getItem('dev_user_preferences');
          const preferences = storedPreferences ? JSON.parse(storedPreferences) : {};
          
          const mockUser: User = {
            id: 'mock-user',
            email: 'user@example.com',
            name: 'Demo User',
            google_id: 'mock-google-id',
            created_time: new Date().toISOString(),
            last_login_time: new Date().toISOString(),
            is_active: true,
            preferences: preferences
          };
          setUser(mockUser);
          return;
        }

        // Check if user is already authenticated
        if (authService.isAuthenticated()) {
          let currentUser = await authService.getCurrentUser();

          // Auto-set timezone from browser if not set on server
          if (currentUser && !currentUser.timezone) {
            const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (browserTz) {
              currentUser = await authService.updateUser({ timezone: browserTz });
            }
          }

          setUser(currentUser);

          // Apply dark mode immediately after getting user data
          if (currentUser?.preferences?.darkMode === true) {
            document.documentElement.classList.add('dark');
            document.body.classList.add('dark');
          }
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        // If auth config fails, assume auth is disabled and create mock user
        // Load stored preferences from localStorage for development mode
        const storedPreferences = localStorage.getItem('dev_user_preferences');
        const preferences = storedPreferences ? JSON.parse(storedPreferences) : {};
        
        const mockUser: User = {
          id: 'mock-user',
          email: 'user@example.com',
          name: 'Demo User',
          google_id: 'mock-google-id',
          created_time: new Date().toISOString(),
          last_login_time: new Date().toISOString(),
          is_active: true,
          preferences: preferences
        };
        setUser(mockUser);
        setAuthConfig({ google_client_id: '', auth_enabled: false });
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (googleToken: string) => {
    try {
      setIsLoading(true);

      // Clear cache before login to prevent showing old user's data
      clearCache();

      const authResponse = await authService.loginWithGoogle(googleToken);

      // Check if this is a different user
      if (user && user.id !== authResponse.user.id) {
        clearCache();
      }

      setUser(authResponse.user);
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: { username: string; email: string; name: string; password: string }) => {
    try {
      setIsLoading(true);
      clearCache();

      const authResponse = await authService.register(userData);
      setUser(authResponse.user);
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithPassword = async (username: string, password: string) => {
    try {
      setIsLoading(true);
      clearCache();

      const authResponse = await authService.loginWithPassword(username, password);

      // Check if this is a different user
      if (user && user.id !== authResponse.user.id) {
        clearCache();
      }

      setUser(authResponse.user);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    // If authentication is disabled, don't actually logout
    if (authConfig && !authConfig.auth_enabled) {
      return;
    }
    
    // Clear cache before logout to prevent showing data after logout
    clearCache();
    
    authService.logout(true);
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      
      // Check if this is a different user
      if (user && currentUser && user.id !== currentUser.id) {
        clearCache();
      }

      setUser(currentUser);
    } catch {
      // If refresh fails, user might be logged out
      clearCache();
      setUser(null);
    }
  };

  const updateUserSettings = async (userData: { name?: string; preferences?: UserPreferences; about_me?: string | null }) => {
    // If authentication is disabled, just update the mock user locally and persist in localStorage
    if (authConfig && !authConfig.auth_enabled) {
      if (user) {
        const updatedUser = {
          ...user,
          ...(userData.name && { name: userData.name }),
          ...(userData.preferences && { preferences: { ...user.preferences, ...userData.preferences } }),
          ...(userData.about_me !== undefined && { about_me: userData.about_me }),
        };
        setUser(updatedUser);
        // Persist user preferences in localStorage for development mode
        localStorage.setItem('dev_user_preferences', JSON.stringify(updatedUser.preferences));
      }
      return;
    }

    const updatedUser = await authService.updateUserSettings(userData);
    setUser(updatedUser);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    authConfig,
    login,
    register,
    loginWithPassword,
    logout,
    refreshUser,
    updateUserSettings,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 